import React, { useMemo, useEffect } from 'react';
import { TimePicker } from 'antd';
import s from './s.module.less';
import { TimeItemType } from 'types/appointmentSetting';
import dayjs from 'dayjs';

type TimeSelectProps = {
    value?: TimeItemType,
    onChange?: (val?: TimeItemType) => void, //start and end time
    duration?: number, //duration in minutes
    disabledTimes?: TimeItemType[], // for multiple selection
    minuteStep?: number,
};

const TimeSelect = (props: TimeSelectProps) => {
    const { onChange, value, duration = 30, disabledTimes, minuteStep } = props;
    const [selectVal, setSelectVal] = React.useState<TimeItemType>();

    useEffect(() => {
        if (value) {
            setSelectVal(value);
        }
    }, [value]);

    const mergeAdjacentTimeRanges = () => {
        if (!disabledTimes || disabledTimes.length === 0) return [];

        const timeRanges = disabledTimes
            .filter((item) => item.fromHour !== undefined && item.toHour !== undefined)
            .map((item) => ({
                start: item.fromHour! * 60 + (item.fromMinute || 0),
                end: item.toHour! * 60 + (item.toMinute || 0),
            }))
            .sort((a, b) => a.start - b.start);

        if (timeRanges.length === 0) return [];

        const mergedRanges = [timeRanges[0]];

        for (let i = 1; i < timeRanges.length; i++) {
            const currentRange = timeRanges[i];
            const lastMergedRange = mergedRanges[mergedRanges.length - 1];

            if (currentRange.start <= lastMergedRange.end) {
                lastMergedRange.end = Math.max(lastMergedRange.end, currentRange.end);
            } else {
                mergedRanges.push(currentRange);
            }
        }

        return mergedRanges;
    };

    const mergedTimeRanges = useMemo(() => mergeAdjacentTimeRanges(), [disabledTimes]);

    const isTimeInDisabledRanges = (hour: number, minute: number) => {
        const currentTime = hour * 60 + minute;
        return mergedTimeRanges.some((range) =>
            currentTime > range.start && currentTime < range.end,
        );
    };

    const isStartTimeDisabled = (hour: number, minute: number) => {
        const currentTime = hour * 60 + minute;

        if (isTimeInDisabledRanges(hour, minute)) {
            return true;
        }

        if (currentTime === 0 && mergedTimeRanges[0]?.start === 0) {
            return true;
        }
        return false;
    };

    const isEndTimeDisabled = (hour: number, minute: number) => {
        const currentTime = hour * 60 + minute;

        if (isTimeInDisabledRanges(hour, minute)) {
            return true;
        }

        const startHour = selectVal?.fromHour;
        const startMinute = selectVal?.fromMinute;

        if (!startHour || !startMinute) {
            return false;
        }

        const startTime = startHour * 60 + startMinute;

        // 结束时间不能早于或等于开始时间
        if (currentTime <= startTime) {
            return true;
        }

        return mergedTimeRanges.some((range) => {
            // 例如，如果已有时间段是1:00-1:30，允许选择1:30-2:00
            if (currentTime === range.end) {
                return false;
            }

            return (startTime < range.end && currentTime > range.start);
        });
    };

    return (
        <div className={s.wrap}>
            <TimePicker
                suffixIcon={null}
                placeholder="Start time"
                value={selectVal?.fromHour !== undefined && selectVal?.fromMinute !== undefined ? dayjs().hour(selectVal.fromHour).minute(selectVal.fromMinute) : undefined}
                use12Hours
                format="h:mm a"
                disabledTime={() => ({
                    disabledHours: () => {
                        const disabledHours = [];
                        for (let i = 0; i < 24; i++) {
                            if (isStartTimeDisabled(i, 0)) {
                                disabledHours.push(i);
                            }
                        }
                        return disabledHours;
                    },
                    disabledMinutes: (hour) => {
                        const disabledMinutes = [];
                        for (let i = 0; i < 60; i += 5) {
                            if (isStartTimeDisabled(hour, i)) {
                                disabledMinutes.push(i);
                            }
                        }
                        return disabledMinutes;
                    },
                })}
                onChange={(val) => {
                    if (!val) {
                        const newItem = { ...selectVal, fromHour: undefined, fromMinute: undefined };
                        setSelectVal(newItem);
                        onChange?.(newItem);
                        return;
                    }

                    // 计算结束时间
                    const endTime = val.add(duration, 'minute');

                    // 检查结束时间是否与禁用时间段冲突
                    const endHour = endTime.hour();
                    const endMinute = endTime.minute();
                    const isEndInDisabled = isTimeInDisabledRanges(endHour, endMinute);

                    // 如果结束时间在禁用时间段内，则不更新
                    if (isEndInDisabled) {
                        return;
                    }

                    const newItem = {
                        fromHour: val.hour(),
                        fromMinute: val.minute(),
                        toHour: endHour,
                        toMinute: endMinute,
                    };
                    setSelectVal(newItem);
                    onChange?.(newItem);
                }}
                minuteStep={5}
            />
            <span className={s.to}>
                to
            </span>

            <TimePicker
                suffixIcon={null}
                placeholder="End time"
                value={selectVal?.toHour !== undefined && selectVal?.toMinute !== undefined ? dayjs().hour(selectVal.toHour).minute(selectVal.toMinute) : undefined}
                use12Hours
                format="h:mm a"
                disabledTime={() => {
                    if (selectVal?.fromHour === undefined || selectVal?.fromMinute === undefined) {
                        return {};
                    }

                    let start = dayjs().hour(selectVal.fromHour).minute(selectVal.fromMinute);
                    if (minuteStep) {
                        start = start.add(minuteStep - 1, 'minute');
                    }

                    return {
                        disabledHours: () => {
                            // 禁用所有在禁用时间段内的小时，以及早于开始时间的小时
                            const disabledHours = [];
                            for (let i = 0; i < 24; i++) {
                                // 如果小时早于开始时间的小时，禁用
                                if (i < start.hour()) {
                                    disabledHours.push(i);
                                    continue;
                                }

                                // 如果是开始时间的小时，在分钟检查中处理
                                if (i === start.hour()) {
                                    continue;
                                }

                                if (isEndTimeDisabled(i, 0)) {
                                    disabledHours.push(i);
                                }
                            }
                            return disabledHours;
                        },
                        disabledMinutes: (hour) => {
                            // 如果是开始时间的小时，禁用所有早于或等于开始时间分钟的时间
                            if (hour === start.hour()) {
                                return Array.from(
                                    { length: Math.ceil((start.minute() + 1) / 5) },
                                    (_, i) => i * 5,
                                ).filter((min) => min <= start.minute());
                            }

                            const disabledMinutes = [];
                            for (let i = 0; i < 60; i += 5) {
                                if (isEndTimeDisabled(hour, i)) {
                                    disabledMinutes.push(i);
                                }
                            }
                            return disabledMinutes;
                        },
                    };
                }}
                onChange={(val) => {
                    if (!val) {
                        const newItem = { ...selectVal, toHour: undefined, toMinute: undefined };
                        setSelectVal(newItem);
                        onChange?.(newItem);
                        return;
                    }
                    const newItem = { ...selectVal, toHour: val.hour(), toMinute: val.minute() };
                    setSelectVal(newItem);
                    onChange?.(newItem);
                }}
                minuteStep={5}
            />
        </div>
    );
};

export default TimeSelect;
